
import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import "./AdminAddTennis.css";

const COLLECTION = "tennismatches";
const TIMEZONE = "Africa/Dar_es_Salaam";
const SETS = [1, 2, 3, 4, 5];

/*
 * Login compatibility with the existing Login.jsx:
 * Prefer Firebase Auth when available; otherwise use the current app
 * login state stored by Login.jsx in localStorage.
 */
function getAppLoginUser() {
  if (auth.currentUser) {
    return {
      uid: auth.currentUser.uid,
      username: auth.currentUser.displayName || "",
      source: "firebase",
    };
  }

  try {
    if (localStorage.getItem("authenticated") !== "true") return null;

    let savedUser = {};
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      try {
        savedUser = JSON.parse(rawUser) || {};
      } catch {
        savedUser = {};
      }
    }

    const username = savedUser.username || localStorage.getItem("username") || "";
    return {
      ...savedUser,
      uid: savedUser.uid || savedUser.id || username,
      username,
      source: "localStorage",
    };
  } catch (error) {
    console.warn("Could not read app login state:", error);
    return null;
  }
}

// Correct score kwa mechi za Best of 3 sets.
// Odds hizi ni mifano; admin anaweza kuzibadilisha.
const DEFAULT_SCORE_ODDS = [
  { score: "2-0", odds: 2.10 },
  { score: "2-1", odds: 3.50 },
  { score: "0-2", odds: 2.30 },
  { score: "1-2", odds: 3.80 },
];

const EMPTY_FORM = {
  country: "",
  league: "",
  homeTeam: "",
  awayTeam: "",
  date: "",
  time: "",
  homeOdds: "",
  awayOdds: "",
};

const EMPTY_SCORE = {
  homeSet1: "",
  awaySet1: "",
  homeSet2: "",
  awaySet2: "",
  homeSet3: "",
  awaySet3: "",
  homeSet4: "",
  awaySet4: "",
  homeSet5: "",
  awaySet5: "",
};

function makeDefaultScoreOdds() {
  return DEFAULT_SCORE_ODDS.map((item) => ({
    score: item.score,
    odds: String(item.odds),
  }));
}

function makeInitialScoreOdds(savedOdds) {
  const source = Array.isArray(savedOdds) ? savedOdds : [];
  const savedMap = new Map(
    source.map((item) => [item.score, item.odds])
  );

  return DEFAULT_SCORE_ODDS.map((item) => ({
    score: item.score,
    odds:
      savedMap.has(item.score) && savedMap.get(item.score) != null
        ? String(savedMap.get(item.score))
        : String(item.odds),
  }));
}

function cleanCell(value) {
  return String(value ?? "")
    .replace(/\*\*/g, "")
    .replace(/<\/?strong>/gi, "")
    .replace(/<\/?b>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function safeNumber(value) {
  if (value === "" || value == null) return null;

  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    return false;
  }

  const date = new Date(`${value}T12:00:00Z`);

  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
}

function getEATDate(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = Number(parts.find((p) => p.type === "year").value);
  const month = Number(parts.find((p) => p.type === "month").value);
  const day = Number(parts.find((p) => p.type === "day").value);

  return new Date(Date.UTC(year, month - 1, day + offsetDays, 12))
    .toISOString()
    .slice(0, 10);
}

function removeMatchId(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/#\s*\d+\s*$/, "")
    .trim();
}

function normaliseTime(value) {
  const clean = removeMatchId(value)
    .replace(/^(Today|Tomorrow)\s+/i, "")
    .trim();

  const match = clean.match(
    /^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i
  );

  if (!match) return "";

  let hour = Number(match[1]);
  const minute = Number(match[2]);

  if (minute > 59) return "";

  if (match[3]) {
    const period = match[3].toUpperCase();

    if (hour < 1 || hour > 12) return "";
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;
  } else if (hour > 23) {
    return "";
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function parseDateTime(value) {
  const clean = removeMatchId(cleanCell(value));

  const match = clean.match(
    /^(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+(.+)$/i
  );

  if (!match) return null;

  const datePart = match[1];
  const time = normaliseTime(match[2]);

  if (!time) return null;

  let date;

  if (/^today$/i.test(datePart)) {
    date = getEATDate(0);
  } else if (/^tomorrow$/i.test(datePart)) {
    date = getEATDate(1);
  } else if (/^\d{4}-/.test(datePart)) {
    const [year, month, day] = datePart.split("-").map(Number);

    date = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  } else {
    const [day, month, rawYear] = datePart.split("/").map(Number);
    const year = rawYear < 100 ? rawYear + 2000 : rawYear;

    date = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  if (!isValidDate(date)) return null;

  return { date, time };
}

function parseLeague(value) {
  const clean = cleanCell(value);
  const parts = clean.split(/\s+-\s+/);

  if (parts.length >= 2) {
    return {
      country: parts[0].trim(),
      league: parts.slice(1).join(" - ").trim(),
    };
  }

  return {
    country: "Tennis",
    league: clean || "Tennis",
  };
}

function isLeagueLine(value) {
  const clean = cleanCell(value);

  return (
    /^[^|]+\s+-\s+[^|]+$/.test(clean) &&
    !/^(Today|Tomorrow)\b/i.test(clean) &&
    !/^\d{1,2}:\d{2}/.test(clean)
  );
}

function extractOdds(value) {
  return (
    String(value || "")
      .replace(/,/g, " ")
      .match(/\d+\.\d{2}/g) || []
  )
    .map(Number)
    .filter((odd) => Number.isFinite(odd) && odd >= 1.01);
}

function parseOdds(value) {
  const odds = extractOdds(value);

  if (odds.length < 2) return null;

  return {
    home: odds[0],
    away: odds[1],
    all: odds,
  };
}

function isDateLine(value) {
  return /^(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+/i.test(
    removeMatchId(value)
  );
}

function parseTennisText(rawText) {
  const lines = String(rawText || "")
    .replace(/\r/g, "")
    .replace(/\u00a0/g, " ")
    .replace(/&nbsp;/gi, " ")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const matches = [];
  let league = "";
  let players = [];
  let oddsForMatch = null;
  let pending = null;

  function savePending(dateTime = null) {
    if (!pending) return;

    if (dateTime) {
      pending.date = dateTime.date;
      pending.time = dateTime.time;
    }

    if (
      pending.homeTeam &&
      pending.awayTeam &&
      pending.date &&
      pending.time &&
      isValidDate(pending.date) &&
      pending.homeTeam.toLowerCase() !== pending.awayTeam.toLowerCase()
    ) {
      matches.push({
        ...pending,
        time: normaliseTime(pending.time),
      });
    }

    pending = null;
  }

  function makePending(homeTeam, awayTeam, odds) {
    if (!league || !homeTeam || !awayTeam || !odds) return;

    const leagueInfo = parseLeague(league);

    pending = {
      country: leagueInfo.country,
      league: leagueInfo.league,
      category: /\bdoubles\b/i.test(league) ? "doubles" : "singles",
      tour: /^\s*ATP\b/i.test(league)
        ? "ATP"
        : /^\s*WTA\b/i.test(league)
        ? "WTA"
        : "Tennis",
      homeTeam: cleanCell(homeTeam),
      awayTeam: cleanCell(awayTeam),
      date: "",
      time: "",
      homeOdds: odds.home,
      awayOdds: odds.away,
      odds: {
        home: odds.home,
        away: odds.away,
        all: odds.all,
      },
      scoreOdds: DEFAULT_SCORE_ODDS.map((item) => ({ ...item })),
    };
  }

  for (const line of lines) {
    if (/^[\s|:\-]+$/.test(line)) continue;

    const rawCells = line.includes("|")
      ? line.replace(/^\|/, "").replace(/\|$/, "").split("|")
      : [line];

    const cells = rawCells.map(cleanCell).filter(Boolean);

    if (!cells.length) continue;

    const leagueCell = cells.find(
      (cell) =>
        isLeagueLine(cell) &&
        !isDateLine(cell) &&
        extractOdds(cell).length === 0 &&
        !/^\+?\d+$/.test(cell)
    );

    if (leagueCell) {
      savePending();
      league = leagueCell;
      players = [];
      oddsForMatch = null;
      continue;
    }

    if (!league) continue;

    const dateCell = cells.find((cell) => isDateLine(cell));

    if (dateCell) {
      const dateTime = parseDateTime(dateCell);

      if (dateTime) {
        if (pending) {
          savePending(dateTime);
        } else if (players.length >= 2 && oddsForMatch) {
          makePending(players[0], players[1], oddsForMatch);
          savePending(dateTime);
        }
      }

      players = [];
      oddsForMatch = null;
      continue;
    }

    const oddsCell = cells.find(
      (cell) => extractOdds(cell).length >= 2
    );

    const names = cells.filter((cell) => {
      if (!cell || cell === oddsCell) return false;
      if (/^\+?\d+$/.test(cell)) return false;
      if (/^#?\d{4,}$/.test(cell)) return false;
      if (/^:?-{2,}:?$/.test(cell)) return false;
      if (/^[+-]?\d+(?:\.\d+)?$/.test(cell)) return false;
      if (isDateLine(cell)) return false;
      if (/^(Country|League|Home|Away|Odds)$/i.test(cell)) return false;

      return true;
    });

    if (names.length) {
      players.push(...names);
      players = players.slice(0, 2);
    }

    if (oddsCell) {
      const parsedOdds = parseOdds(oddsCell);

      if (parsedOdds) {
        oddsForMatch = parsedOdds;

        if (players.length >= 2) {
          makePending(players[0], players[1], parsedOdds);
          players = [];
          oddsForMatch = null;
        }
      }
    }
  }

  savePending();

  return matches;
}

function calculateSets(score) {
  let homeSets = 0;
  let awaySets = 0;
  let playedSets = 0;
  let foundEmptySet = false;

  for (const setNumber of SETS) {
    const home = safeNumber(score[`homeSet${setNumber}`]);
    const away = safeNumber(score[`awaySet${setNumber}`]);

    if (home == null && away == null) {
      foundEmptySet = true;
      continue;
    }

    // Set mpya haiwezi kujazwa baada ya set iliyoachwa wazi.
    if (foundEmptySet) {
      return {
        valid: false,
        homeSets,
        awaySets,
        playedSets,
        winner: null,
      };
    }

    if (
      home == null ||
      away == null ||
      !Number.isInteger(home) ||
      !Number.isInteger(away) ||
      home < 0 ||
      away < 0 ||
      home === away
    ) {
      return {
        valid: false,
        homeSets,
        awaySets,
        playedSets,
        winner: null,
      };
    }

    playedSets += 1;

    if (home > away) homeSets += 1;
    else awaySets += 1;
  }

  return {
    valid: true,
    homeSets,
    awaySets,
    playedSets,
    winner:
      homeSets === awaySets
        ? null
        : homeSets > awaySets
        ? "home"
        : "away",
  };
}

function makeInitialScore(score = {}) {
  const result = {};

  for (const setNumber of SETS) {
    result[`homeSet${setNumber}`] =
      score[`homeSet${setNumber}`] ?? "";
    result[`awaySet${setNumber}`] =
      score[`awaySet${setNumber}`] ?? "";
  }

  return result;
}

function buildScoreOdds(scoreOdds) {
  const result = [];
  const seen = new Set();

  for (const item of scoreOdds) {
    const odds = safeNumber(item.odds);

    if (odds == null || odds < 1.01) {
      return {
        valid: false,
        message: `Weka odds sahihi kwa correct score ${item.score}.`,
      };
    }

    if (seen.has(item.score)) {
      return {
        valid: false,
        message: `Correct score ${item.score} imejirudia.`,
      };
    }

    seen.add(item.score);
    result.push({
      score: item.score,
      odds,
    });
  }

  return { valid: true, data: result };
}

export default function AdminTennis() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [score, setScore] = useState(EMPTY_SCORE);
  const [scoreOdds, setScoreOdds] = useState(makeDefaultScoreOdds);
  const [matches, setMatches] = useState([]);
  const [bulkText, setBulkText] = useState("");
  const [search, setSearch] = useState("");
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const preview = useMemo(
    () => parseTennisText(bulkText),
    [bulkText]
  );

  const liveSetResult = useMemo(
    () => calculateSets(score),
    [score]
  );

  async function loadMatches() {
    setLoading(true);
    setError("");

    try {
      const snapshot = await getDocs(collection(db, COLLECTION));

      const rows = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      rows.sort((a, b) =>
        `${a.date || ""} ${a.time || ""}`.localeCompare(
          `${b.date || ""} ${b.time || ""}`
        )
      );

      setMatches(rows);
    } catch (err) {
      setError(err?.message || "Imeshindikana kusoma mechi za Tennis.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMatches();
  }, []);

  function handleFormChange(event) {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }

  function handleScoreOddsChange(index, value) {
    setScoreOdds((current) =>
      current.map((item, i) =>
        i === index ? { ...item, odds: value } : item
      )
    );
  }

  function buildOdds(home, away) {
    const homeOdds = safeNumber(home);
    const awayOdds = safeNumber(away);

    if (
      homeOdds == null ||
      awayOdds == null ||
      homeOdds < 1.01 ||
      awayOdds < 1.01
    ) {
      return null;
    }

    return {
      home: homeOdds,
      away: awayOdds,
      all: [homeOdds, awayOdds],
    };
  }

  function buildMatchDocument(match, odds, correctScoreOdds) {
    return {
      sport: "tennis",
      country: match.country,
      league: match.league,
      category: match.category || "singles",
      tour: match.tour || "Tennis",
      homeTeam: match.homeTeam,
      awayTeam: match.awayTeam,
      A: match.homeTeam,
      B: match.awayTeam,
      date: match.date,
      time: normaliseTime(match.time),
      timezone: TIMEZONE,

      // Odds za mshindi wa mechi
      homeOdds: odds.home,
      awayOdds: odds.away,
      odds: {
        home: odds.home,
        away: odds.away,
        all: odds.all,
      },

      // Correct score odds za tennis
      scoreOdds: correctScoreOdds,

      // Matokeo halisi ya seti hujazwa baada ya mechi.
      score: {
        homeSet1: null,
        awaySet1: null,
        homeSet2: null,
        awaySet2: null,
        homeSet3: null,
        awaySet3: null,
        homeSet4: null,
        awaySet4: null,
        homeSet5: null,
        awaySet5: null,
        homeSets: 0,
        awaySets: 0,
        winner: null,
      },

      status: "upcoming",
      source: "admin-tennis",
      createdBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  }

  async function addSingleMatch(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    const loginUser = getAppLoginUser();
    if (!loginUser) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    if (
      !form.country.trim() ||
      !form.league.trim() ||
      !form.homeTeam.trim() ||
      !form.awayTeam.trim() ||
      !isValidDate(form.date) ||
      !normaliseTime(form.time)
    ) {
      setError("Jaza taarifa zote na uhakikishe tarehe na muda ni sahihi.");
      return;
    }

    if (
      form.homeTeam.trim().toLowerCase() ===
      form.awayTeam.trim().toLowerCase()
    ) {
      setError("Majina ya wachezaji hayawezi kuwa sawa.");
      return;
    }

    const odds = buildOdds(form.homeOdds, form.awayOdds);

    if (!odds) {
      setError("Weka odds mbili sahihi, angalau 1.01 kila moja.");
      return;
    }

    const correctScores = buildScoreOdds(scoreOdds);

    if (!correctScores.valid) {
      setError(correctScores.message);
      return;
    }

    setSaving(true);

    try {
      const match = {
        country: form.country.trim(),
        league: form.league.trim(),
        category: /doubles/i.test(form.league) ? "doubles" : "singles",
        tour: /^ATP/i.test(form.league)
          ? "ATP"
          : /^WTA/i.test(form.league)
          ? "WTA"
          : "Tennis",
        homeTeam: form.homeTeam.trim(),
        awayTeam: form.awayTeam.trim(),
        date: form.date,
        time: form.time,
      };

      await addDoc(
        collection(db, COLLECTION),
        buildMatchDocument(match, odds, correctScores.data)
      );

      setForm(EMPTY_FORM);
      setScoreOdds(makeDefaultScoreOdds());
      setSuccess("Mechi na correct score odds zimehifadhiwa.");
      await loadMatches();
    } catch (err) {
      setError(err?.message || "Imeshindikana kuhifadhi mechi.");
    } finally {
      setSaving(false);
    }
  }

  async function importBulkMatches(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    const loginUser = getAppLoginUser();
    if (!loginUser) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    const parsed = parseTennisText(bulkText);

    if (!parsed.length) {
      setError(
        "Hakuna mechi zilizotambuliwa. Hakikisha kila mechi ina ligi, wachezaji wawili, odds na muda."
      );
      return;
    }

    setBulkSaving(true);
    let saved = 0;

    try {
      for (const match of parsed) {
        const odds = {
          home: match.homeOdds,
          away: match.awayOdds,
          all: match.odds?.all || [
            match.homeOdds,
            match.awayOdds,
          ],
        };

        const correctScores = DEFAULT_SCORE_ODDS.map((item) => ({
          ...item,
        }));

        await addDoc(
          collection(db, COLLECTION),
          buildMatchDocument(match, odds, correctScores)
        );

        saved += 1;
      }

      setBulkText("");
      setSuccess(
        `Mechi ${saved} zimehifadhiwa pamoja na correct score odds.`
      );
      await loadMatches();
    } catch (err) {
      setError(
        `${saved} zimehifadhiwa kabla ya hitilafu. ${err?.message || ""}`
      );
      await loadMatches();
    } finally {
      setBulkSaving(false);
    }
  }

  function selectMatch(match) {
    setSelectedMatchId(match.id);
    setScore(makeInitialScore(match.score || {}));
    setScoreOdds(makeInitialScoreOdds(match.scoreOdds));
    setError("");
    setSuccess("");

    document.getElementById("tennis-score-editor")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  async function saveScore() {
    setError("");
    setSuccess("");

    const loginUser = getAppLoginUser();
    if (!loginUser) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    if (!selectedMatchId) {
      setError("Chagua mechi kwanza.");
      return;
    }

    const result = calculateSets(score);

    if (!result.valid) {
      setError(
        "Kagua seti: kila set iliyoingizwa iwe na pointi za wachezaji wote wawili, zisizolingana. Usiache seti katikati."
      );
      return;
    }

    const selected = matches.find(
      (match) => match.id === selectedMatchId
    );

    const targetSets =
      selected?.category === "best-of-five" ||
      selected?.bestOf === 5
        ? 3
        : 2;

    if (
      !result.winner ||
      Math.max(result.homeSets, result.awaySets) < targetSets
    ) {
      setError(`Mshindi anahitaji kushinda seti ${targetSets}.`);
      return;
    }

    const savedScore = {};

    for (const setNumber of SETS) {
      savedScore[`homeSet${setNumber}`] = safeNumber(
        score[`homeSet${setNumber}`]
      );
      savedScore[`awaySet${setNumber}`] = safeNumber(
        score[`awaySet${setNumber}`]
      );
    }

    savedScore.homeSets = result.homeSets;
    savedScore.awaySets = result.awaySets;
    savedScore.winner = result.winner;

    const correctScores = buildScoreOdds(scoreOdds);

    if (!correctScores.valid) {
      setError(correctScores.message);
      return;
    }

    setSaving(true);

    try {
      await updateDoc(doc(db, COLLECTION, selectedMatchId), {
        score: savedScore,
        scoreOdds: correctScores.data,
        status: "finished",
        winner:
          result.winner === "home"
            ? selected?.homeTeam || selected?.A
            : selected?.awayTeam || selected?.B,
        updatedAt: serverTimestamp(),
        resultUpdatedBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
        resultSavedAt: serverTimestamp(),
      });

      setSelectedMatchId("");
      setScore(EMPTY_SCORE);
      setScoreOdds(makeDefaultScoreOdds());
      setSuccess("Matokeo na correct score odds yamehifadhiwa.");
      await loadMatches();
    } catch (err) {
      setError(err?.message || "Imeshindikana kuhifadhi matokeo.");
    } finally {
      setSaving(false);
    }
  }

  const filteredMatches = matches.filter((match) =>
    [
      match.country,
      match.league,
      match.homeTeam,
      match.awayTeam,
      match.A,
      match.B,
      match.tour,
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const selectedMatch = matches.find(
    (match) => match.id === selectedMatchId
  );

  return (
    <main className="admin-tennis-page">
      <header className="at-header">
        <div>
          <span className="at-eyebrow">ADMIN / SPORTS</span>
          <h1>Admin Tennis</h1>
          <p>ATP, WTA, Singles, Doubles na Correct Score Odds.</p>
        </div>

        <div className="at-count">
          <strong>{matches.length}</strong>
          <span>Matches</span>
        </div>
      </header>

      {error && <div className="at-alert at-error">{error}</div>}
      {success && <div className="at-alert at-success">{success}</div>}

      <section className="at-panel">
        <div className="at-panel-heading">
          <div>
            <h2>Paste / Import Mechi Nyingi</h2>
            <p>Mechi zinazoingizwa hapa zitapewa correct score odds za mwanzo.</p>
          </div>
        </div>

        <form onSubmit={importBulkMatches}>
          <label className="at-field">
            Bandika bookmaker hapa
            <textarea
              className="at-textarea"
              value={bulkText}
              onChange={(event) => setBulkText(event.target.value)}
              placeholder={`ATP - ATP Shanghai, China Men Singles
+20
Tien, Learner
Ruud, Casper
1.891.85 1.692.05 1.572.25
Tomorrow 07:00#10111`}
              rows={14}
              required
            />
          </label>

          <div className="at-actions">
            <button
              className="at-primary"
              type="submit"
              disabled={bulkSaving || preview.length === 0}
            >
              {bulkSaving ? "Ina-import..." : `Import Mechi ${preview.length}`}
            </button>

            <button
              className="at-secondary"
              type="button"
              onClick={() => setBulkText("")}
              disabled={bulkSaving}
            >
              Futa Orodha
            </button>
          </div>
        </form>

        <p className="at-note">
          Mechi zilizoingizwa kwa bulk zitapewa odds za correct score
          zilizo hapa chini. Unaweza kuzibadilisha kwa kuchagua mechi
          kwenye orodha.
        </p>

        <div className="at-preview">
          <h3>Preview ({preview.length})</h3>

          {preview.length === 0 ? (
            <p className="at-empty">Bandika mechi ili kuona Preview.</p>
          ) : (
            <div className="at-list">
              {preview.map((match, index) => (
                <article
                  className="at-card"
                  key={`${match.homeTeam}-${match.awayTeam}-${index}`}
                >
                  <div className="at-meta">
                    <span>{match.tour}</span>
                    <span>{match.league}</span>
                    <span>{match.category}</span>
                  </div>

                  <div className="at-teams">
                    <strong>{match.homeTeam}</strong>
                    <span>VS</span>
                    <strong>{match.awayTeam}</strong>
                  </div>

                  <div className="at-foot">
                    <span>{match.date} · {match.time} EAT</span>
                  </div>

                  <div className="at-foot">
                    <span>Match odds: {match.odds.all.join(" / ")}</span>
                  </div>

                  <div className="at-correct-score">
                    <strong>Correct Score Odds</strong>
                    <div className="at-correct-score-grid">
                      {DEFAULT_SCORE_ODDS.map((item) => (
                        <span key={item.score}>
                          {item.score} @ {item.odds.toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="at-panel">
        <div className="at-panel-heading">
          <div>
            <h2>Ongeza Mechi Moja</h2>
            <p>Ingiza taarifa za mechi na odds zake.</p>
          </div>
        </div>

        <form className="at-form" onSubmit={addSingleMatch}>
          <label>
            Tour / Country
            <input
              name="country"
              value={form.country}
              onChange={handleFormChange}
              placeholder="ATP / WTA"
              required
            />
          </label>

          <label>
            Ligi
            <input
              name="league"
              value={form.league}
              onChange={handleFormChange}
              placeholder="ATP Shanghai Men Singles"
              required
            />
          </label>

          <label>
            Mchezaji wa kwanza
            <input
              name="homeTeam"
              value={form.homeTeam}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Mchezaji wa pili
            <input
              name="awayTeam"
              value={form.awayTeam}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Tarehe
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Muda (EAT)
            <input
              type="time"
              name="time"
              value={form.time}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Odds za mshindi wa kwanza
            <input
              type="number"
              min="1.01"
              step="0.01"
              name="homeOdds"
              value={form.homeOdds}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Odds za mshindi wa pili
            <input
              type="number"
              min="1.01"
              step="0.01"
              name="awayOdds"
              value={form.awayOdds}
              onChange={handleFormChange}
              required
            />
          </label>

          <div className="at-correct-score-editor">
            <h3>Correct Score Odds — Best of 3</h3>
            <p>
              Weka odds za kila matokeo. Hizi ni odds za matokeo ya mwisho
              ya seti, si pointi za games ndani ya seti.
            </p>

            <div className="at-correct-score-grid">
              {scoreOdds.map((item, index) => (
                <label key={item.score}>
                  Score {item.score}
                  <input
                    type="number"
                    min="1.01"
                    step="0.01"
                    value={item.odds}
                    onChange={(event) =>
                      handleScoreOddsChange(index, event.target.value)
                    }
                    required
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="at-actions">
            <button className="at-primary" type="submit" disabled={saving}>
              {saving ? "Inahifadhi..." : "Hifadhi Mechi na Odds"}
            </button>

            <button
              className="at-secondary"
              type="button"
              onClick={() => {
                setForm(EMPTY_FORM);
                setScoreOdds(makeDefaultScoreOdds());
              }}
            >
              Futa Form
            </button>
          </div>
        </form>
      </section>

      <section className="at-panel" id="tennis-score-editor">
        <div className="at-panel-heading">
          <div>
            <h2>Ingiza Matokeo na Hariri Correct Score Odds</h2>
            <p>Chagua mechi kisha rekebisha odds au jaza set scores.</p>
          </div>
        </div>

        {selectedMatch ? (
          <div className="at-selected">
            <span>Mechi iliyochaguliwa</span>
            <strong>
              {selectedMatch.homeTeam || selectedMatch.A} VS{" "}
              {selectedMatch.awayTeam || selectedMatch.B}
            </strong>
          </div>
        ) : (
          <p className="at-note">
            Chagua mechi kwenye orodha iliyo chini.
          </p>
        )}

        <div className="at-correct-score-editor">
          <h3>Correct Score Odds</h3>
          <div className="at-correct-score-grid">
            {scoreOdds.map((item, index) => (
              <label key={item.score}>
                Score {item.score}
                <input
                  type="number"
                  min="1.01"
                  step="0.01"
                  value={item.odds}
                  onChange={(event) =>
                    handleScoreOddsChange(index, event.target.value)
                  }
                  disabled={!selectedMatchId || saving}
                  required
                />
              </label>
            ))}
          </div>
        </div>

        <div className="at-sets">
          {SETS.map((setNumber) => (
            <div className="at-set" key={setNumber}>
              <h4>Set {setNumber}</h4>

              <label>
                {selectedMatch?.homeTeam || "Player 1"}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={score[`homeSet${setNumber}`]}
                  onChange={(event) =>
                    setScore((current) => ({
                      ...current,
                      [`homeSet${setNumber}`]: event.target.value,
                    }))
                  }
                  disabled={!selectedMatchId || saving}
                />
              </label>

              <label>
                {selectedMatch?.awayTeam || "Player 2"}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={score[`awaySet${setNumber}`]}
                  onChange={(event) =>
                    setScore((current) => ({
                      ...current,
                      [`awaySet${setNumber}`]: event.target.value,
                    }))
                  }
                  disabled={!selectedMatchId || saving}
                />
              </label>
            </div>
          ))}
        </div>

        <div className="at-selected">
          <span>Set result preview</span>
          <strong>
            {liveSetResult.homeSets} — {liveSetResult.awaySets}
          </strong>
        </div>

        <div className="at-actions">
          <button
            className="at-primary"
            type="button"
            onClick={saveScore}
            disabled={!selectedMatchId || saving}
          >
            {saving ? "Inahifadhi..." : "Hifadhi Odds na Matokeo"}
          </button>

          <button
            className="at-secondary"
            type="button"
            onClick={() => {
              setSelectedMatchId("");
              setScore(EMPTY_SCORE);
              setScoreOdds(makeDefaultScoreOdds());
            }}
          >
            Futa Score Form
          </button>
        </div>
      </section>

      <section className="at-panel">
        <div className="at-panel-heading at-list-heading">
          <div>
            <h2>Mechi Zilizohifadhiwa</h2>
            <p>Mechi kutoka Firebase collection tennismatches.</p>
          </div>

          <button
            className="at-secondary"
            type="button"
            onClick={loadMatches}
            disabled={loading}
          >
            {loading ? "Inapakia..." : "Refresh"}
          </button>
        </div>

        <input
          className="at-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Tafuta ATP, WTA au mchezaji..."
        />

        {loading ? (
          <p className="at-empty">Inapakia mechi...</p>
        ) : filteredMatches.length === 0 ? (
          <p className="at-empty">Hakuna mechi zilizopatikana.</p>
        ) : (
          <div className="at-list">
            {filteredMatches.map((match) => {
              const currentScore = match.score || {};
              const isSelected = selectedMatchId === match.id;
              const allOdds =
                match.odds?.all ||
                [match.odds?.home, match.odds?.away].filter(
                  (odd) => odd != null
                );

              const correctOdds = Array.isArray(match.scoreOdds)
                ? match.scoreOdds
                : [];

              return (
                <article className="at-card" key={match.id}>
                  <div className="at-meta">
                    <span>{match.tour || "Tennis"}</span>
                    <span>{match.league || "—"}</span>
                    <span>{match.status || "upcoming"}</span>
                  </div>

                  <div className="at-teams">
                    <div>
                      <strong>{match.homeTeam || match.A || "Player 1"}</strong>
                      <small>{currentScore.homeSets ?? 0} sets</small>
                    </div>

                    <span>VS</span>

                    <div>
                      <strong>{match.awayTeam || match.B || "Player 2"}</strong>
                      <small>{currentScore.awaySets ?? 0} sets</small>
                    </div>
                  </div>

                  <div className="at-foot">
                    <span>{match.date || "—"} · {match.time || "—"}</span>
                    <span>{match.category || "singles"}</span>
                  </div>

                  <div className="at-foot">
                    <span>
                      Match odds: {allOdds.length ? allOdds.join(" / ") : "—"}
                    </span>
                    {match.winner && <span>Mshindi: {match.winner}</span>}
                  </div>

                  <div className="at-correct-score">
                    <strong>Correct Score Odds</strong>
                    {correctOdds.length ? (
                      <div className="at-correct-score-grid">
                        {correctOdds.map((item) => (
                          <span key={item.score}>
                            {item.score} @ {Number(item.odds).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="at-note">
                        Mechi hii haina correct score odds zilizohifadhiwa.
                        Chagua mechi ili kuongeza.
                      </p>
                    )}
                  </div>

                  <button
                    className={isSelected ? "at-secondary" : "at-primary"}
                    type="button"
                    onClick={() => selectMatch(match)}
                    disabled={saving}
                  >
                    {isSelected ? "Mechi Imechaguliwa" : "Hariri Odds / Ingiza Score"}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <p className="at-note">
        Firebase collection: <code>tennismatches</code>
      </p>
    </main>
  );
}
